// src/pages/LoginPage.jsx
import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { login, requestPasswordReset } from "../services/api";
import { setAuth } from "../services/authStorage";
import { AuthContext } from "../context/AuthContext";
import { departmentDashboardPath, normalizeRole } from "../utils/roles";

const LoginPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useContext(AuthContext);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetIdentifier, setResetIdentifier] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await login({ username, password });
      if (!response?.token) {
        throw new Error("Authentication failed");
      }

      const role = normalizeRole(response?.user?.role || "user");
      setAuth({ token: response.token, role });
      await refreshUser();
      navigate(departmentDashboardPath(role), { replace: true });
    } catch (err) {
      setError(err.message || "Unable to log in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openResetDialog = () => {
    setResetIdentifier(username);
    setResetMessage("");
    setResetError("");
    setResetLoading(false);
    setResetOpen(true);
  };

  const closeResetDialog = () => {
    setResetLoading(false);
    setResetOpen(false);
  };

  const handleRequestReset = async (event) => {
    event.preventDefault();
    setResetError("");
    setResetMessage("");
    setResetLoading(true);

    try {
      const identifier = resetIdentifier.trim();
      if (!identifier) {
        setResetError("Please enter your email or username.");
        return;
      }

      await requestPasswordReset({ identifier });
      setResetMessage("If an account matches, a reset link has been sent to the email on file.");
    } catch (err) {
      setResetError(err.message || "Unable to process your request. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-10">
      <div className="grid w-full max-w-5xl gap-10 md:grid-cols-2">
        <div className="hidden md:flex flex-col justify-between text-white/80">
          <div>
            <h1 className="text-4xl font-semibold text-white">Ecoops Wealth Portal</h1>
            <p className="mt-4 max-w-sm text-sm text-slate-200/80">
              Manage investments, monitor wallet activity, and stay ahead with curated opportunities tailored
              to your financial goals.
            </p>
          </div>
          <div className="space-y-2 text-sm">
            <p className="text-slate-300/80">Secure. Transparent. Designed for serious investors.</p>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              <span>24/7 access to your portfolio from anywhere.</span>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-white/40 p-8">
          <h2 className="text-2xl font-semibold text-slate-900">Welcome back</h2>
          <p className="mt-2 text-sm text-slate-500">Sign in to continue to your Ecoops Wealth dashboard.</p>

          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="mt-6 space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700" htmlFor="username">
                Email or phone number
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                autoComplete="username"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 pr-24 text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 my-auto rounded-md px-2 text-sm font-medium text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  aria-pressed={showPassword}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="flex justify-end text-sm">
              <button
                type="button"
                onClick={openResetDialog}
                className="font-medium text-emerald-600 hover:text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 rounded-md px-1 py-0.5"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:bg-emerald-400"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            <span>New to Ecoops Wealth? </span>
            <Link to="/onboarding/contact" className="font-medium text-emerald-600 hover:text-emerald-700">
              Start onboarding
            </Link>
          </div>
        </div>
      </div>

      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset your password</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Enter the email you use to sign in and we&apos;ll send you a password reset link.
                </p>
              </div>
              <button
                type="button"
                onClick={closeResetDialog}
                className="rounded-full p-1 text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                aria-label="Close password reset dialog"
              >
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>

            <form onSubmit={handleRequestReset} className="mt-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="reset-identifier">
                  Email address
                </label>
                <input
                  id="reset-identifier"
                  type="text"
                  value={resetIdentifier}
                  onChange={(event) => setResetIdentifier(event.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  placeholder="you@example.com"
                />
              </div>

              {resetError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
                  {resetError}
                </div>
              )}

              {resetMessage && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-600">
                  {resetMessage}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeResetDialog}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:bg-emerald-400"
                >
                  {resetLoading ? "Sending..." : "Send reset link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
